import {
  getBrandNameById,
  getCanonicalPagePath,
  getCurrentLang,
  toAbsoluteUrl,
} from "@platformx/utilities";

const stripUndefined = (obj: Record<string, any>): Record<string, any> =>
  Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));

const buildShareObjectFields = (contentType: string, doc: any): Record<string, any> => {
  switch (contentType?.toLowerCase()) {
    case "article":
      return stripUndefined({
        page_name: doc?.page_name,
        banner: doc?.banner,
        sub_title: doc?.sub_title,
        content_type: doc?.content_type,
        page_tags: doc?.page_tags,
        link_tags: doc?.link_tags,
        article_content: doc?.article_content,
        tag: doc?.tag,
        links: doc?.links,
        published_images: doc?.published_images,
        original_image: doc?.original_image,
        show_author: doc?.show_author,
      });
    case "quiz":
      return stripUndefined({
        background_content: doc?.background_content,
        display_scores: doc?.display_scores,
        questions: doc?.questions,
        result_range_1: doc?.result_range_1,
        result_range_2: doc?.result_range_2,
        result_range_3: doc?.result_range_3,
        result_range_4: doc?.result_range_4,
        published_images: doc?.published_images,
        original_image: doc?.original_image,
      });
    case "event":
      return stripUndefined({
        // Event save always sends it; the whole-document replace would otherwise wipe it.
        background_content: doc?.background_content,
        banner_image: doc?.banner_image,
        thumbnail_image: doc?.thumbnail_image,
        actual_address: doc?.actual_address,
        event_end_date: doc?.event_end_date,
        event_start_date: doc?.event_start_date,
        virtual_address: doc?.virtual_address,
        google_api_address: doc?.google_api_address,
        locality: doc?.locality,
        postal_code: doc?.postal_code,
        region_state: doc?.region_state,
        country: doc?.country,
        published_images: doc?.published_images,
        original_image: doc?.original_image,
      });
    case "vod":
      return stripUndefined({
        VodAccountId: doc?.VodAccountId,
        PlayerID: doc?.PlayerID,
        PlayerType: doc?.PlayerType,
        VodVideoId: doc?.VodVideoId,
        Thumbnail: doc?.Thumbnail,
        Poster: doc?.Poster,
        VodState: doc?.VodState,
        DsapceVideoUrl: doc?.DsapceVideoUrl,
        Author: doc?.Author,
        show_author: doc?.show_author,
        published_images: doc?.published_images,
        original_image: doc?.original_image,
      });
    case "poll":
      return stripUndefined({
        background_content: doc?.background_content,
        display_scores: doc?.display_scores,
        question_background_content: doc?.question_background_content,
        poll_description: doc?.poll_description,
        poll_question: doc?.poll_question,
        poll_result: doc?.poll_result,
        poll_title: doc?.poll_title,
        options_compound_fields: doc?.options_compound_fields,
        start_date: doc?.start_date,
        end_date: doc?.end_date,
        published_images: doc?.published_images,
        original_image: doc?.original_image,
      });
    default:
      return {};
  }
};

// The social-share and SEO URLs stored in `settings` (and the JSON-LD `url`) are copies of the
// canonical made at save time. When the primary brand changes they must follow it, otherwise the
// edit screen keeps showing the old brand in them.
const rebrandSettings = (settings: any, canonicalPath?: string) =>
  canonicalPath && settings && typeof settings === "object"
    ? { ...settings, socialog_url: canonicalPath, socialog_twitter_url: canonicalPath }
    : settings;

export const rebrandStructureData = (structureData: any, canonicalPath?: string) => {
  if (!canonicalPath || typeof structureData !== "string" || !structureData) return structureData;
  try {
    const parsed = JSON.parse(structureData);
    // Only the auto-generated markup is derived from the canonical; `manual` is the editor's own.
    if (!parsed?.auto?.url) return structureData;
    return JSON.stringify({
      ...parsed,
      auto: { ...parsed.auto, url: toAbsoluteUrl(canonicalPath) },
    });
  } catch {
    return structureData;
  }
};

export const buildShareWithBrandsInput = (
  contentType: string,
  doc: any,
  path: string,
  primaryBrandId: string,
  secondaryBrandIds: string[] = [],
  pageState: string | undefined = undefined,
): { CommonFields: Record<string, any>; ObjectFields: Record<string, any> } => {
  // Shared docs carry ONE canonical, always built from the primary brand (KIWI-6401).
  const canonicalPath = doc?.slug
    ? getCanonicalPagePath(
        doc.slug,
        doc?.language || getCurrentLang(),
        contentType?.toLowerCase(),
        getBrandNameById(primaryBrandId),
      )
    : undefined;
  return {
    CommonFields: stripUndefined({
      analytics: doc?.analytics,
      analytics_enable: doc?.analytics_enable,
      category: doc?.category,
      createdBy: doc?.createdBy || "",
      creationDate: doc?.creationDate || new Date().toISOString(),
      current_page_url: doc?.current_page_url || `/${path}`,
      description: doc?.description,
      is_edit: doc?.is_edit,
      modificationDate: new Date().toISOString(),
      others: doc?.others,
      page: doc?.page || path,
      page_state: doc?.page_state || doc?.status || pageState,
      parent_page_url: doc?.parent_page_url || "/",
      robot_txt: doc?.robot_txt,
      seo_enable: doc?.seo_enable,
      settings: rebrandSettings(doc?.settingsProperties || doc?.settings, canonicalPath),
      short_description: doc?.short_description,
      short_title: doc?.short_title,
      site_name: doc?.site_name,
      sitemap: doc?.sitemap,
      structure_data: rebrandStructureData(doc?.structure_data, canonicalPath),
      tags: doc?.tags,
      tag_sm: doc?.tag_sm,
      title: doc?.title,
      is_featured: doc?.is_featured,
      slug: doc?.slug,
      content_behind_rendering: doc?.content_behind_rendering,
      notification: doc?.notification,
      notification_title: doc?.notification_title,
      notification_desc: doc?.notification_desc,
      notification_channel: doc?.notification_channel,
      notification_topics: doc?.notification_topics,
      notification_image: doc?.notification_image,
      page_createdby: doc?.page_createdby || doc?.createdBy,
      page_lastmodifiedby: doc?.page_lastmodifiedby || doc?.last_modifiedBy || doc?.createdBy || "",
      canonical_url: canonicalPath ?? doc?.canonical_url,
      brand_name: primaryBrandId,
      secondary_brandname: secondaryBrandIds,
    }),
    ObjectFields: buildShareObjectFields(contentType, doc),
  };
};
